import React, { useMemo } from 'react';
import { FmlxButton } from 'fmlx-common-ui';
import {
  useImageManagementTasks,
  useSearchTargetRelationsOld,
  useSearchTargetsOldVersion,
  useSelectedImageActionId,
  useSelectedImageTaskId,
} from 'redux/hooks/imageManagementTask';
import { useOnChangeActionConstraints } from '../hooks';
import { excludeKeyFromArrayAndStringify, getCurrentDateISOString } from 'utils/Global';
import {
  constraintsDisplayName,
  constraintsInitialState,
  constraintsTarget,
  filterNoneOption,
  imageTypeShortNameOptions,
  noneOption,
} from '../constants';
import {
  SearchFieldDTO,
  SearchTargetDTO,
} from '../../../../../../business/models/Models';
import QueryBuilder, { Option, OptionList, RuleGroupArray } from 'react-querybuilder';
import {
  classTypeMappingToInputTypeMapping,
  defaultConstraintsOperators,
} from '../../../../../querybuilder';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import {
  useImagingSettingsDTO,
  useScoreDefinitionDTO,
  useScoreGroupsDTO,
} from 'redux/hooks/imagingSetup';
import { EPlateState, GetPlateStateLabel } from 'enums/EPlatestate';
import {
  AddGroupButton,
  AddRulesButton,
  CombinatorDropdown,
  FilterOptions,
  FilterPresetValues,
  FixedFieldText,
  OperatorDropdown,
  RemoveButton,
  ValueInput,
} from './QueryComponents';
import { FilterModalItem } from './AddFilterModal';
import { injectIds } from '../utils';
import { nanoid } from '@reduxjs/toolkit';
import { getFieldGroup, getFieldLabel } from './utils';

// Unified filter panel that contains Basic and Advanced filter
export function FilterPanel() {
  const imageTasks = useImageManagementTasks();
  const selectedImageTaskId = useSelectedImageTaskId();
  const selectedActionId = useSelectedImageActionId();
  const imagingSettings = useImagingSettingsDTO();
  const scoreDefinitions = useScoreDefinitionDTO();
  const searchTargets = useSearchTargetsOldVersion();
  const scoreGroups = useScoreGroupsDTO();
  const onChangeConstraints = useOnChangeActionConstraints();

  const selectedAction = useMemo(() => {
    const currentAction = imageTasks
      ?.find((task) => task.id === selectedImageTaskId)
      ?.imageManagementActionList?.find((actn) => actn.id === selectedActionId);
    return currentAction;
  }, [selectedActionId, selectedImageTaskId, imageTasks]);

  // query
  const constraints = useMemo(() => {
    const resolvedConstraints = injectIds(
      selectedAction
        ? { ...selectedAction.constraints, disabled: false }
        : { ...constraintsInitialState, disabled: true },
    );
    resolvedConstraints.rules = resolvedConstraints.rules.map((rule) => ({ ...rule }));
    return resolvedConstraints;
  }, [selectedAction, imageTasks]);

  // list of options
  const targetRelations: { [key: string]: SearchTargetDTO[] } =
    useSearchTargetRelationsOld();
  const selectedSearchTargetId: number = searchTargets.find(
    (target) => target.name === constraintsTarget.imageSetting,
  )?.id;

  // options
  const targetsRelationsOptionsList: OptionList = useMemo(() => {
    if (!selectedSearchTargetId || !targetRelations[selectedSearchTargetId]) {
      return [];
    }

    return targetRelations[selectedSearchTargetId].map((st: SearchTargetDTO) => {
      const optionsList = st.searchFields.map(
        (sf: SearchFieldDTO): Option => ({
          name: `${sf.id}`,
          label: sf.fieldAndTableDisplayName,
          selectionLabel: sf.fieldAndTableDisplayName,
          datatype: sf.fieldClass,
          inputType: classTypeMappingToInputTypeMapping[sf.fieldClass],
          isDropdown: false,
        }),
      );

      return { label: st.name, options: optionsList };
    });
  }, [targetRelations, selectedSearchTargetId]);

  // Basic Filter fields
  const basicFilterFields = useMemo(() => {
    const getSearchTargetField = (searchTarget: string, searchField: string) => {
      const target = searchTargets.find((target) => target.name === searchTarget);
      return target?.searchFields.find((field) => field.displayName === searchField);
    };

    return {
      scoreGroupField: getSearchTargetField(
        constraintsTarget.score,
        constraintsDisplayName.scoreGroups,
      ),
      scoreField: getSearchTargetField(
        constraintsTarget.score,
        constraintsDisplayName.score,
      ),
      imagingSettingsField: getSearchTargetField(
        constraintsTarget.imageSetting,
        constraintsDisplayName.imageSetting,
      ),
      dateImagedField: getSearchTargetField(
        constraintsTarget.imageSetting,
        constraintsDisplayName.dateImaged,
      ),
      imageTypeShortNameField: getSearchTargetField(
        constraintsTarget.image,
        constraintsDisplayName.imageTypeShortName,
      ),
      plateStateField: getSearchTargetField(
        constraintsTarget.plate,
        constraintsDisplayName.plateState,
      ),
    };
  }, [searchTargets]);

  // Score Group basic filter preset values
  const scoreGroupOptions: FilterOptions[] = useMemo(() => {
    let index = 0;
    const list: FilterOptions[] = scoreGroups.map((scoreGroup): FilterOptions => {
      return {
        id: ++index,
        value: scoreGroup.name,
        text: scoreGroup.name,
        group: '',
      };
    });
    list?.unshift({ ...filterNoneOption });
    return list;
  }, [scoreGroups]);

  // Score basic filter preset values
  const scoreOptions: FilterOptions[] = useMemo(() => {
    let index = 0;
    const list: FilterOptions[] = scoreDefinitions.map((score): FilterOptions => {
      const groupName =
        score.scoreGroupName ??
        scoreGroups.find((sg) => sg.scoreGroupsId === score.wellDropScoreGroupId)?.name ??
        '';
      return {
        id: ++index,
        value: score.scoreName,
        text: score.scoreName,
        group: groupName,
      };
    });
    list?.unshift({ ...filterNoneOption });
    return list;
  }, [scoreDefinitions, scoreGroups]);

  // Score Group basic filter preset values
  const imagingSettingsOptions: FilterOptions[] = useMemo(() => {
    return [
      filterNoneOption,
      ...(imagingSettings?.captureProfilesDto ?? []).map((cp) => ({
        id: cp.id,
        value: cp.name,
        text: cp.name,
        group: '',
      })),
    ];
  }, [imagingSettings?.captureProfilesDto]);

  // Plate State basic filter preset values
  const PLATE_STATE_FILTER = new Set([
    EPlateState.Imaged,
    EPlateState.Expired,
    EPlateState.CrystalHarvested,
  ]);
  const PlateStateOptions: FilterOptions[] = [
    ...Object.values(EPlateState)
      .filter(
        (plateState): plateState is EPlateState =>
          typeof plateState === 'number' && PLATE_STATE_FILTER.has(plateState),
      )
      .map((plateState) => ({
        id: plateState,
        value: String(plateState),
        text: GetPlateStateLabel(plateState),
        group: '',
      })),
  ];

  // All basic filter preset values
  const BasicFilterPresetValues: Record<number, FilterPresetValues> = useMemo(() => {
    const presets: Record<number, FilterPresetValues> = {};
    if (basicFilterFields.scoreGroupField?.id != null && scoreGroupOptions?.length > 0) {
      presets[basicFilterFields.scoreGroupField.id] = {
        values: scoreGroupOptions,
        defaultValue: noneOption.value,
      };
    }
    if (basicFilterFields.scoreField?.id != null && scoreOptions?.length > 0) {
      presets[basicFilterFields.scoreField.id] = {
        values: scoreOptions,
        defaultValue: noneOption.value,
      };
    }
    if (
      basicFilterFields.imagingSettingsField?.id != null &&
      imagingSettingsOptions?.length > 0
    ) {
      presets[basicFilterFields.imagingSettingsField.id] = {
        values: imagingSettingsOptions,
        defaultValue: noneOption.value,
      };
    }
    if (basicFilterFields.imageTypeShortNameField?.id != null) {
      presets[basicFilterFields.imageTypeShortNameField.id] = {
        values: imageTypeShortNameOptions,
        defaultValue: noneOption.value,
      };
    }
    if (basicFilterFields.plateStateField?.id != null) {
      presets[basicFilterFields.plateStateField.id] = {
        values: PlateStateOptions,
        defaultValue: noneOption.value,
      };
    }
    return presets;
  }, [
    basicFilterFields,
    scoreGroupOptions,
    scoreOptions,
    imagingSettingsOptions,
    imageTypeShortNameOptions,
    PlateStateOptions,
  ]);

  const getBasicFilterDisplayText = (filter) => {
    if (!filter) {
      return '';
    }
    return getFieldLabel(filter.fieldAndTableDisplayName as any, true);
  };

  // Basic Filters that are shown in add filter modal
  const BasicFilters: FilterModalItem[] = useMemo(() => {
    return [
      {
        id: basicFilterFields.scoreGroupField?.id,
        field: basicFilterFields.scoreGroupField?.name,
        displayText: getBasicFilterDisplayText(basicFilterFields.scoreGroupField),
        isDropdown: true,
      },
      {
        id: basicFilterFields.scoreField?.id,
        field: basicFilterFields.scoreField?.name,
        disabled: false,
        displayText: getBasicFilterDisplayText(basicFilterFields.scoreField),
        isDropdown: true,
        parentId: basicFilterFields.scoreGroupField?.id,
      },
      {
        id: basicFilterFields.imagingSettingsField?.id,
        field: basicFilterFields.imagingSettingsField?.name,
        displayText: getBasicFilterDisplayText(basicFilterFields.imagingSettingsField),
        isDropdown: true,
      },
      {
        id: basicFilterFields.dateImagedField?.id,
        field: basicFilterFields.dateImagedField?.name,
        displayText: getBasicFilterDisplayText(basicFilterFields.dateImagedField),
        defaultCombinator: '<',
        defaultValue: getCurrentDateISOString(),
        isDropdown: false,
      },
      {
        id: basicFilterFields.imageTypeShortNameField?.id,
        field: basicFilterFields.imageTypeShortNameField?.name,
        displayText: getBasicFilterDisplayText(basicFilterFields.imageTypeShortNameField),
        defaultValue: 'fl',
        isDropdown: true,
      },
      {
        id: basicFilterFields.plateStateField?.id,
        field: basicFilterFields.plateStateField?.name,
        displayText: getBasicFilterDisplayText(basicFilterFields.plateStateField),
        defaultValue: '5',
        isDropdown: true,
      },
    ];
  }, [basicFilterFields]);

  // Advanced Filters that are shown in add filter modal
  const AdvancedFilters: FilterModalItem[] = useMemo(() => {
    return targetsRelationsOptionsList.flatMap((group) => {
      return group.options.map((field) => {
        return {
          id: Number(field.name),
          field: getFieldLabel(field.label as any, false) ?? field.label,
          groupName: getFieldGroup(field.label as any) ?? group.label,
        };
      });
    });
  }, [targetsRelationsOptionsList]);

  const existingBasicFilterIds = useMemo(() => {
    const ids = new Set<number>();
    const basicFilterIdSet = new Set(BasicFilters.map((f) => f.id));

    const collectFieldIds = (rules: any) => {
      for (const rule of rules) {
        if ('rules' in rule) {
          collectFieldIds(rule.rules);
        } else {
          const fieldId = Number(rule.field);
          if (basicFilterIdSet.has(fieldId) && rule.isDropdown) {
            ids.add(fieldId);
          }
        }
      }
    };

    if (constraints.rules) {
      collectFieldIds(constraints.rules as RuleGroupArray);
    }

    return ids;
  }, [constraints.rules, BasicFilters]);

  const handleOnQueryChange = (query) => {
    if (!selectedAction) {
      return;
    }
    if (
      query?.combinator !== constraints.combinator ||
      // makesure the content is changed
      excludeKeyFromArrayAndStringify(query?.rules, 'id') !=
        excludeKeyFromArrayAndStringify(constraints?.rules as any, 'id')
    ) {
      onChangeConstraints(query);
    }
  };

  const onAddQuery = (selectedFilters: FilterModalItem[], path: number[]) => {
    const traverseQueryAndAddFields = (
      rules: RuleGroupArray,
      path: number[],
      newRules: any[],
    ): RuleGroupArray => {
      if (path.length === 0) {
        return [...rules, ...newRules];
      }

      const [index, ...rest] = path;
      const current = rules[index];

      if (!current || !('rules' in current)) {
        return rules; // dead end
      }

      return rules.map((rule, i) => {
        if (i !== index) {
          return rule;
        }

        if (!('rules' in rule)) {
          return rule; // dead end
        }

        return {
          ...rule,
          rules: traverseQueryAndAddFields(rule.rules, rest, newRules),
        };
      });
    };

    if (selectedFilters.length > 0) {
      const newFields = selectedFilters.map((field) => {
        return {
          id: nanoid(),
          field: field.id,
          value: field.defaultValue ?? '',
          operator: field.defaultCombinator ?? '=',
          isDropdown: field.isDropdown,
        };
      });
      const updatedRules = traverseQueryAndAddFields(
        constraints.rules as any,
        path,
        newFields,
      );
      const updatedConstraints = {
        ...constraints,
        rules: updatedRules,
      };
      handleOnQueryChange(updatedConstraints);
    }
  };

  const onResetFilter = (e) => {
    const resetConstraints = injectIds({ ...constraintsInitialState, disabled: false });
    onChangeConstraints(resetConstraints);
  };

  return (
    <>
      <div id="filterpanel-container" className="flex flex-col h-full w-full m-2 gap-3">
        <div
          id="filterpanel-resetfilter-wrapper"
          className="flex justify-between items-center"
        >
          <div id="filterpanel-resetfilter-title" className="text-sm font-bold">
            Filters
          </div>
          <FmlxButton
            id={`filterpanel-resetfilter-button`}
            label="RESET FILTER"
            type={FmlxButton.Type.BASIC}
            variant={FmlxButton.Variant.OUTLINE}
            onClick={onResetFilter}
          />
        </div>
        <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend }}>
          <QueryBuilder
            getOperators={() => defaultConstraintsOperators}
            fields={targetsRelationsOptionsList}
            query={constraints}
            onQueryChange={handleOnQueryChange}
            context={useMemo(
              () => ({
                basicFilters: BasicFilters,
                advancedFilters: AdvancedFilters,
                basicFilterFields: basicFilterFields,
                basicFilterPresetValues: BasicFilterPresetValues,
                queryRules: constraints.rules,
                onAddQuery: onAddQuery,
                existingBasicFilterIds: existingBasicFilterIds,
              }),
              [
                BasicFilters,
                AdvancedFilters,
                basicFilterFields,
                BasicFilterPresetValues,
                constraints.rules,
                onAddQuery,
              ],
            )}
            controlClassnames={{
              queryBuilder:
                'flex h-full w-full overflow-auto border rounded-lg p-2 justify-item-start',
              ruleGroup:
                'border-x-0 divide-y w-full rounded-none border-[#E0E0E0] mt-0 pr-0 pl-4',
              header: 'flex flex-row w-full items-center my-0',
              removeGroup: 'flex-1 min-w-0 justify-items-end justify-end',
              rule: 'flex items-center w-full gap-2',
              fields: 'flex-none w-[34vw] max-w-[34%]',
              operators: 'flex-1 min-w-0',
              value: 'flex-none w-[35vw] max-w-[35%]',
              removeRule: 'ml-auto flex-none',
            }}
            controlElements={{
              combinatorSelector: CombinatorDropdown,
              addRuleAction: AddRulesButton,
              addGroupAction: AddGroupButton,
              removeGroupAction: RemoveButton,
              removeRuleAction: RemoveButton,
              valueEditor: ValueInput,
              operatorSelector: OperatorDropdown,
              fieldSelector: FixedFieldText,
            }}
          />
        </QueryBuilderDnD>
      </div>
    </>
  );
}
